// Resumen de horario legible a partir del `empresa_perfil.horario`
// (objeto jsonb de 7 días: { lunes: { abre, cierra, cerrado }, ... }) y del
// flag `mostrar_horario` que indica si atiende presencialmente.

const ETIQUETAS = {
  lunes: 'Lun',
  martes: 'Mar',
  miercoles: 'Mié',
  jueves: 'Jue',
  viernes: 'Vie',
  sabado: 'Sáb',
  domingo: 'Dom',
};

const DIAS = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];

/**
 * Devuelve true si el horario debe mostrarse: el flag `mostrar_horario` no es
 * false (el usuario no lo desactivó por ser tienda 100% online) y existe al
 * menos un día con horario de apertura configurado.
 * @param {object} perfil perfil de empresa (empresa_perfil)
 * @returns {boolean}
 */
export function debeMostrarHorario(perfil) {
  const perfilObj = perfil ?? {};
  if (perfilObj.mostrar_horario === false) return false;
  const dias = normalizarHorario(perfilObj.horario);
  return DIAS.some((d) => !dias[d].cerrado);
}

/**
 * Estructura segura de horario: 7 días × { abre, cierra, cerrado }.
 * @param {object|null|undefined} horario
 * @returns {object}
 */
export function normalizarHorario(horario) {
  const entrada = horario && typeof horario === 'object' ? horario : {};
  const resultado = {};
  for (const d of DIAS) {
    const dia = entrada[d] ?? {};
    resultado[d] = {
      abre: typeof dia.abre === 'string' && dia.abre ? dia.abre : '',
      cierra: typeof dia.cierra === 'string' && dia.cierra ? dia.cierra : '',
      cerrado: Boolean(dia.cerrado),
    };
  }
  return resultado;
}

/**
 * Agrupa días contiguos con el mismo horario en rangos para una lectura
 * compacta (p. ej. "Lun–Vie 09:00–18:00 · Sáb 09:00–13:00"). Soporta tanto el
 * horario de 7 días nuevo como el string plano heredado.
 * @param {object} perfil perfil de empresa (empresa_perfil)
 * @returns {string}
 */
export function formatearHorario(perfil) {
  const perfilObj = perfil ?? {};
  // Compatibilidad: si horario es una cadena, se devuelve tal cual.
  if (typeof perfilObj.horario === 'string') {
    return perfilObj.horario.trim();
  }

  const dias = normalizarHorario(perfilObj.horario);
  const diasAbiertos = DIAS.filter((d) => !dias[d].cerrado && dias[d].abre && dias[d].cierra);

  if (diasAbiertos.length === 0) {
    // Sin horario por día: si era todo "cerrado", no muestres residuos.
    return '';
  }

  // Agrupar rangos contiguos con el mismo rango horario.
  const grupos = [];
  for (const dia of diasAbiertos) {
    const obj = dias[dia];
    const clave = `${obj.abre}-${obj.cierra}`;
    const ultimo = grupos[grupos.length - 1];
    if (
      ultimo &&
      ultimo.clave === clave &&
      ultimo.indices[ultimo.indices.length - 1] === DIAS.indexOf(dia) - 1
    ) {
      ultimo.indices.push(DIAS.indexOf(dia));
    } else {
      grupos.push({ clave, indices: [DIAS.indexOf(dia)] });
    }
  }

  return grupos
    .map((g) => {
      const etiquetas = g.indices.map((i) => ETIQUETAS[DIAS[i]]);
      const rangoDias =
        etiquetas.length === 1
          ? etiquetas[0]
          : `${etiquetas[0]}–${etiquetas[etiquetas.length - 1]}`;
      const [abre, cierra] = g.clave.split('-');
      return `${rangoDias} ${abre}–${cierra}`;
    })
    .join(' · ');
}
